import React, { forwardRef, useCallback, useMemo } from "react";
import {
  Box,
  DefaultProps,
  Input,
  ScrollArea,
  useInputProps,
} from "@mantine/core";

import ReactSelect, {
  components,
  StylesConfig,
  OptionProps,
  SingleValueProps,
} from "react-select";
import ReactSelectAsync from "react-select/async";
import { BaseSelectProps, BaseSelectStylesNames, SelectItem } from "./types";

export interface SelectSharedProps<Item, Value> {
  /** Select data used to render items in dropdown */
  data?: ReadonlyArray<string | Item>;

  loadData?: (inputValue: string, callback: (items: any[]) => void) => void;

  /** Nothing found label */
  nothingFound?: React.ReactNode;

  /** Set to true to enable search */
  searchable?: boolean;

  /** Allow to clear item */
  clearable?: boolean;

  /** Controlled input onChange handler */
  onChange?(value: string): void;

  /** Controlled input onChange handler */
  onSelectItem?(value: Item): void;

  /** Function to get selected item template*/
  selectedItemTemplate?(itemData: any): React.ReactNode;

  /** Function to get item template*/
  itemTemplate?(itemData: any): React.ReactNode;

  /** Header template*/
  header?: React.ReactNode;

  /** Footer template*/
  footer?: React.ReactNode;

  value?: string | null | undefined;

  zIndex?: React.CSSProperties["zIndex"];

  loading?: boolean;
}

export interface SelectPlusProps
  extends DefaultProps<BaseSelectStylesNames>,
    BaseSelectProps,
    SelectSharedProps<SelectItem, string | null> {}

const defaultProps: Partial<SelectPlusProps> = {
  required: false,
  size: "sm",
  zIndex: 2,
};

const Control = ({ children, ...props }: any) => (
  <components.Control {...props}>{children}</components.Control>
);

const DropdownIndicator = ({ children, ...props }: any) => (
  <components.DropdownIndicator {...props}>
    {children}
  </components.DropdownIndicator>
);

const Menu = ({ children, ...props }: any) => (
  <components.Menu {...props}>
    <Box p={5}>{children}</Box>
  </components.Menu>
);

export const SelectPlus = forwardRef<HTMLInputElement, SelectPlusProps>(
  (props, ref) => {
    const {
      inputProps,
      wrapperProps,
      value,
      data,
      loadData,
      onChange,
      onSelectItem,
      searchable = false,
      clearable,
      selectedItemTemplate,
      itemTemplate,
      header = null,
      footer = null,
      placeholder,
      zIndex = 2,
      loading,
    } = useInputProps("Select", defaultProps, props);

    const modirStyles: StylesConfig<SelectItem> = useMemo(
      () => ({
        control: (styles) => ({
          display: "flex",
          position: "relative",
          justifyContent: "space-between",
        }),
        valueContainer: (styles) => ({
          alignItems: "center",
          display: "grid",
          flex: "1 1 0%",
          position: "relative",
          overflow: "hidden",
          padding: "0 10px",
        }),
        input: (styles) => ({
          ...styles,
          margin: 0,
          padding: 0,
          visibility: "visible",
          flex: "1 1 auto",
          display: "inline-grid",
          gridArea: "1 / 1 / 2 / 3",
          gridTemplateColumns: "0px min-content",
          boxSizing: "border-box",
        }),
        indicatorsContainer: (styles) => ({ ...styles }),
        dropdownIndicator: (styles) => ({ ...styles }),
        singleValue: (styles) => ({
          gridArea: "1 / 1 / 2 / 3",
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }),
        menu: (styles) => ({ ...styles, left: 0, zIndex: zIndex }),
        menuList: (styles) => ({ ...styles }),
        option: (styles, { data, isDisabled, isFocused, isSelected }) => {
          return {
            ...styles,
            padding: "5px 10px",
          };
        },
      }),
      [zIndex]
    );

    const MenuList = useCallback(
      ({ children, ...props }: any) => (
        <Box>
          {header}
          {/* <ScrollArea style={{ height: "100%" }} type="auto" offsetScrollbars> */}
          <components.MenuList {...props}>{children}</components.MenuList>
          {/* </ScrollArea> */}
          {footer}
        </Box>
      ),
      [header, footer]
    );

    const Option = useCallback((props: OptionProps<any>) => {
      const { children, data } = props;
      const hasItemTemplate = typeof itemTemplate === "function";
      const item = hasItemTemplate ? itemTemplate(data) : null;
      return (
        <components.Option {...props}>
          {item ?? <Box>{children}</Box>}
        </components.Option>
      );
    }, []);

    const SingleValue = useCallback((props: SingleValueProps<any>) => {
      const { children, data } = props;
      const hasTemplate = typeof selectedItemTemplate === "function";
      const item = hasTemplate ? selectedItemTemplate(data) : null;
      return (
        <components.SingleValue {...props}>
          {item ?? <Box>{children}</Box>}
        </components.SingleValue>
      );
    }, []);

    const ReactSelectComponent = useCallback(
      ({ ...props }: any) => (
        <ReactSelect
          styles={modirStyles}
          {...props}
          components={{
            Control,
            DropdownIndicator,
            Menu,
            MenuList,
            Option,
            SingleValue,
          }}
        />
      ),
      [header, footer]
    );

    const ReactSelectAsyncComponent = useCallback(
      ({ options, loadOptions, ...props }: any) => (
        <ReactSelectAsync
          styles={modirStyles}
          {...props}
          loadOptions={loadOptions}
          defaultOptions={options}
          components={{
            Control,
            DropdownIndicator,
            Menu,
            MenuList,
            Option,
            SingleValue,
          }}
        />
      ),
      []
    );

    const selectedValue = data?.find(
      (item: any) => item.value === value?.toString()
    );

    return (
      <Input.Wrapper {...wrapperProps}>
        <Input
          sx={{
            ">div": {
              padding: 0,
            },
          }}
          autoComplete="off"
          {...inputProps}
          component={
            loadData ? ReactSelectAsyncComponent : ReactSelectComponent
          }
          options={data}
          loadOptions={loadData}
          onChange={(val: any) => {
            onChange?.(val?.value);
            onSelectItem?.(val);
          }}
          value={selectedValue}
          isClearable={clearable}
          isSearchable={searchable}
          placeholder={placeholder}
          isLoading={loading}
        />
      </Input.Wrapper>
    );
  }
);
